import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, notification, Select, Table, Tag } from "antd";

// Constants
import { PAGE, PAGE_NAME } from "@utils/constants";
import { replaceUrlByParams } from "@utils/api";

// Layouts
import StickyBreadcrumbPageComponent from "@layouts/stickyBreadcrumbPage/stickyBreadcrumbPage";

// APIs
import { customFieldDisplayValue, customFieldParams, listCustomFieldDefs, listRecords } from "@apis/erent2Application";

const BREADCRUMB = [
  { name: PAGE_NAME.ERENT2_APPLICATION_LIST, path: PAGE.ERENT2_APPLICATION_LIST },
];

const PAGE_SIZE = 20;

const REGION_OPTIONS = [
  { value: "central-and-western", label: "中西區" },
  { value: "eastern", label: "東區" },
  { value: "southern", label: "南區" },
  { value: "wan-chai", label: "灣仔區" },
  { value: "kowloon-city", label: "九龍城區" },
  { value: "kwun-tong", label: "觀塘區" },
  { value: "sham-shui-po", label: "深水埗區" },
  { value: "wong-tai-sin", label: "黃大仙區" },
  { value: "yau-tsim-mong", label: "油尖旺區" },
  { value: "islands", label: "離島區" },
  { value: "kwai-tsing", label: "葵青區" },
  { value: "north", label: "北區" },
  { value: "sai-kung", label: "西貢區" },
  { value: "sha-tin", label: "沙田區" },
  { value: "tai-po", label: "大埔區" },
  { value: "tsuen-wan", label: "荃灣區" },
  { value: "tuen-mun", label: "屯門區" },
  { value: "yuen-long", label: "元朗區" },
];

const GROUP_OPTIONS = [
  { value: "A", label: "A 組" },
  { value: "B", label: "B 組" },
  { value: "C", label: "C 組" },
];

const GROUP_COLOR = { A: "blue", B: "green", C: "gold" };

// ponytail: group is server-derived from region; client only renders it.
const renderGroupTag = (val) => {
  const opt = GROUP_OPTIONS.find((g) => g.value === val);
  if (!opt) return "—";
  return <Tag color={GROUP_COLOR[val]}>{opt.label}</Tag>;
};

const renderCustomValue = (value) => Array.isArray(value)
  ? value.map((item) => <Tag key={item}>{item}</Tag>)
  : customFieldDisplayValue(value);

const COLUMNS = (onView, definitions) => {
  const fixed = [
  { title: "編號", dataIndex: "identity", key: "identity" },
  { title: "姓名", dataIndex: "fullName", key: "fullName" },
  { title: "電話", dataIndex: "phoneNumber", key: "phoneNumber" },
  {
    title: "地區",
    dataIndex: "region",
    key: "region",
    render: (val) =>
      REGION_OPTIONS.find((r) => r.value === val)?.label ?? val,
  },
  {
    title: "組別",
    dataIndex: "group",
    key: "group",
    render: renderGroupTag,
  },
  { title: "感興趣產品", dataIndex: "interestedProduct", key: "interestedProduct" },
  {
    title: "操作",
    key: "action",
    render: (_, record) => (
      <Button type="link" onClick={() => onView(record._id)}>
        查看詳情
      </Button>
    ),
  },
  ];
  fixed.splice(fixed.length - 1, 0, ...definitions.map((definition) => ({
    title: definition.label,
    key: `custom-${definition.key}`,
    render: (_, record) => renderCustomValue(record.customFields?.[definition.key]),
  })));
  return fixed;
};

function Erent2ApplicationListPage() {
  const history = useHistory();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [definitions, setDefinitions] = useState([]);
  const [customFilters, setCustomFilters] = useState({});

  const [filterRegion, setFilterRegion] = useState(undefined);
  const [filterProduct, setFilterProduct] = useState("");
  const [filterGroup, setFilterGroup] = useState(undefined);

  const fetchRecords = useCallback(
    async (currentPage, region, interestedProduct, group, fields = customFilters) => {
      setLoading(true);
      try {
        const params = { page: currentPage, limit: PAGE_SIZE };
        if (region) params.region = region;
        if (interestedProduct) params.interestedProduct = interestedProduct;
        if (group) params.group = group;
        Object.assign(params, customFieldParams(fields));

        const res = await listRecords(params);
        setRecords(res.data ?? []);
        setTotal(res.pagination?.total ?? 0);
      } catch {
        notification.error({ message: "載入申請列表失敗，請重試" });
      } finally {
        setLoading(false);
      }
    },
    [customFilters]
  );

  useEffect(() => {
    fetchRecords(page, filterRegion, filterProduct, filterGroup);
    listCustomFieldDefs()
      .then((response) => setDefinitions(response.data || response || []))
      .catch(() => notification.error({ message: "載入自訂欄位失敗，請重試" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = () => {
    setPage(1);
    fetchRecords(1, filterRegion, filterProduct, filterGroup);
  };

  const onPageChange = (newPage) => {
    setPage(newPage);
    fetchRecords(newPage, filterRegion, filterProduct, filterGroup);
  };

  const onView = (id) => {
    history.push(replaceUrlByParams(PAGE.ERENT2_APPLICATION_DETAIL, { id }));
  };

  return (
    <StickyBreadcrumbPageComponent breadcrumb={BREADCRUMB}>
      <div />
      <div style={{ padding: "16px 24px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <Select
            allowClear
            placeholder="篩選地區"
            style={{ width: 160 }}
            value={filterRegion}
            onChange={setFilterRegion}
          >
            {REGION_OPTIONS.map((r) => (
              <Select.Option key={r.value} value={r.value}>
                {r.label}
              </Select.Option>
            ))}
          </Select>
          <Select
            allowClear
            placeholder="篩選組別"
            style={{ width: 120 }}
            value={filterGroup}
            onChange={setFilterGroup}
          >
            {GROUP_OPTIONS.map((g) => (
              <Select.Option key={g.value} value={g.value}>
                {g.label}
              </Select.Option>
            ))}
          </Select>
          <Input
            placeholder="感興趣產品"
            style={{ width: 200 }}
            value={filterProduct}
            onChange={(e) => setFilterProduct(e.target.value)}
            onPressEnter={onSearch}
          />
          {definitions.map((definition) => definition.type === "text" ? <Input
            key={definition._id}
            placeholder={definition.label}
            style={{ width: 180 }}
            value={customFilters[definition.key] || ""}
            onChange={(event) => setCustomFilters({ ...customFilters, [definition.key]: event.target.value })}
            onPressEnter={onSearch}
          /> : <Select
            key={definition._id}
            allowClear
            mode={definition.type === "multiselect" ? "multiple" : undefined}
            placeholder={definition.label}
            style={{ minWidth: 160 }}
            value={customFilters[definition.key]}
            onChange={(value) => setCustomFilters({ ...customFilters, [definition.key]: value })}
          >{(definition.options || []).map((option) => <Select.Option key={option} value={option}>{option}</Select.Option>)}</Select>)}
          <Button type="primary" onClick={onSearch}>
            搜尋
          </Button>
        </div>
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={records}
          columns={COLUMNS(onView, definitions)}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: onPageChange,
            showTotal: (t) => `共 ${t} 筆`,
            hideOnSinglePage: false,
          }}
        />
        {total === 0 && !loading && (
          <div style={{ textAlign: "right", padding: "8px 0", color: "rgba(0,0,0,0.45)" }}>
            共 0 筆
          </div>
        )}
      </div>
    </StickyBreadcrumbPageComponent>
  );
}

export default Erent2ApplicationListPage;
